import Vue from "vue";

/**
 * v-truncated
 *
 * Reports whether an element's rendered text is being clipped (line-clamped or
 * single-line ellipsized). Useful for conditionally enabling a tooltip / "See
 * more" link only when there is actually hidden content.
 *
 * The directive measures `scrollHeight` vs `clientHeight` (and `scrollWidth`
 * vs `clientWidth`) and calls back with `true` when the visible box is smaller
 * than the intrinsic content size. Re-measures automatically on element
 * resize (ResizeObserver) and on parent component re-renders (componentUpdated).
 *
 * Requirements
 *   - The element must have CSS that can clip its content. Common setups:
 *       Multi-line clamp:
 *         display: -webkit-box;
 *         -webkit-box-orient: vertical;
 *         -webkit-line-clamp: N (number of lines);
 *         line-clamp: N (number of lines);
 *         overflow: hidden;
 *         overflow-wrap: anywhere;
 *   - The directive value must be a function: `(isTruncated) => { ... }`.
 *
 * Usage with Vuetify v-tooltip
 *
 *   <template>
 *     <v-tooltip bottom :disabled="!truncated.employerName">
 *       <template v-slot:activator="{ on, attrs }">
 *         <span
 *           v-bind="attrs"
 *           v-on="on"
 *           class="text-clamp"
 *           v-truncated="(t) => (truncated.key = t)"
 *         >
 *           {{ value }}
 *         </span>
 *       </template>
 *       <span>{{ value }}</span>
 *     </v-tooltip>
 *   </template>
 *
 *   <script>
 *   export default {
 *     data() {
 *       return {
 *         // pre-declare each key so Vue 2 reactivity picks them up
 *         truncated: {
 *           key: false,
 *         },
 *       };
 *     },
 *   };
 *   </script>
 *
 * Usage with a "See more" link
 *
 *   <span v-truncated="(t) => (isTruncated = t)" class="value-clamp">
 *     {{ longText }}
 *   </span>
 *   <a v-if="isTruncated" @click="expanded = true">See more</a>
 *
 * Notes
 *   - Apply only to elements whose CSS actually clips. Without clipping, the
 *     callback will always receive `false`.
 *   - Do not place on the same element as `v-if`; toggling the directive's
 *     element in/out re-runs `bind`/`unbind` and resets state. Place it on a
 *     stable element inside the `v-if` block instead.
 *   - On Vue 2 you must declare nested keys (e.g. `truncated.employerName`) up
 *     front in `data()`, otherwise the reactivity won't propagate. Use
 *     `this.$set(this.truncated, key, false)` if adding keys dynamically.
 *   - The 1px tolerance in the comparison absorbs sub-pixel rounding so
 *     perfectly-fitting text is not reported as truncated.
 */
Vue.directive("truncated", {
  bind: function (el, binding) {
    el.truncatedUpdate = function () {
      var truncated =
        el.scrollHeight > el.clientHeight + 1 ||
        el.scrollWidth > el.clientWidth + 1;
      if (typeof binding.value === "function") {
        binding.value(truncated);
      }
    };
    el.truncatedObserver = new ResizeObserver(el.truncatedUpdate);
    el.truncatedObserver.observe(el);
    requestAnimationFrame(el.truncatedUpdate);
  },
  componentUpdated: function (el) {
    if (el.truncatedUpdate) el.truncatedUpdate();
  },
  unbind: function (el) {
    if (el.truncatedObserver) el.truncatedObserver.disconnect();
    el.truncatedUpdate = null;
    el.truncatedObserver = null;
  },
});
